import React, { useState } from "react";

const CONDITIONS = [
  {
    value: "crystal_clear",
    label: "Crystal clear",
    description: "Sparkling blue water; you can see right to the bottom.",
    image: "/images/pool-condition/pool-condition-1-crystal-clear.jpg",
  },
  {
    value: "slightly_cloudy",
    label: "Slightly cloudy",
    description: "Still blue but a bit hazy; bottom is visible but less crisp.",
    image: "/images/pool-condition/pool-condition-2-slightly-cloudy.jpg",
  },
  {
    value: "cloudy_light_green",
    label: "Cloudy and light green",
    description: "Green-teal tint; the bottom is only partially visible.",
    image: "/images/pool-condition/pool-condition-3-cloudy-light-green.jpg",
  },
  {
    value: "green",
    label: "Green",
    description: "Opaque green water with visible algae; bottom isn't visible.",
    image: "/images/pool-condition/pool-condition-4-green.jpg",
  },
  {
    value: "swamp",
    label: "Swamp",
    description: "Dark green or black water with heavy algae and surface debris.",
    image: "/images/pool-condition/pool-condition-5-swamp.jpg",
  },
];

const SlidePoolCondition = ({ onNext, onBack, formData = {} }) => {
  const initialIndex = (() => {
    const v = formData.poolCondition;
    if (!v) return 0;
    const idx = CONDITIONS.findIndex((c) => c.value === v);
    return idx >= 0 ? idx : 0;
  })();
  const [index, setIndex] = useState(initialIndex);
  const current = CONDITIONS[index];

  const handleSlide = (e) => setIndex(parseInt(e.target.value, 10));
  const handleTickClick = (i) => setIndex(i);

  const handleSubmit = (e) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    onNext(current.value, {
      poolCondition: current.value,
      poolConditionLabel: current.label,
      poolConditionIndex: index,
    });
  };

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit}>
        <div className="step-section">
          <h1>How is your water looking?</h1>
          <div className="subtitle">
            <span>Slide to match your pool's current condition. A pool can look clearer than it really is as debris and chemistry settle to the bottom. Your tech will confirm the live conditions with a full inspection on the first visit.</span>
          </div>
        </div>

        <div className="pool-condition-container">
          <div className="pool-condition-image-wrap">
            {CONDITIONS.map((c, i) => (
              <img
                key={c.value}
                src={c.image}
                alt={c.label}
                className={`pool-condition-image ${i === index ? "active" : ""}`}
                loading="eager"
                decoding="sync"
                draggable={false}
              />
            ))}
          </div>

          <div className="pool-condition-label">{current.label}</div>

          <input
            type="range"
            min="0"
            max={CONDITIONS.length - 1}
            step="1"
            value={index}
            onChange={handleSlide}
            className="pool-condition-slider swiper-no-swiping"
            aria-label="Pool condition"
            aria-valuetext={current.label}
          />

          <div className="pool-condition-ticks swiper-no-swiping">
            {CONDITIONS.map((c, i) => (
              <button
                key={c.value}
                type="button"
                className={`pool-condition-tick ${i === index ? "active" : ""}`}
                onClick={() => handleTickClick(i)}
                aria-label={`Select ${c.label}`}
              >
                <span className="pool-condition-tick-dot" />
              </button>
            ))}
          </div>

          <p className="pool-condition-description">{current.description}</p>
        </div>

        <button type="submit" className="btn btn-next">Next</button>
        <input type="hidden" id="progress_slide_1" data-width="50%" />
      </form>

      <style jsx>{`
        .pool-condition-container {
          width: 100%;
          max-width: 560px;
          margin: 0 auto;
          padding: 8px 0 16px;
          box-sizing: border-box;
        }
        .pool-condition-image-wrap {
          position: relative;
          width: 100%;
          aspect-ratio: 3 / 2;
          padding-top: 66.6667%;
          border-radius: 14px;
          overflow: hidden;
          background: #f3f4f6;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
        }
        @supports (aspect-ratio: 1 / 1) {
          .pool-condition-image-wrap {
            padding-top: 0;
          }
        }
        .pool-condition-image {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 250ms ease;
          user-select: none;
          -webkit-user-drag: none;
        }
        .pool-condition-image.active {
          opacity: 1;
        }
        .pool-condition-label {
          margin-top: 18px;
          text-align: center;
          font-size: 18px;
          font-weight: 600;
          color: var(--primary-text);
        }
        .pool-condition-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 6px;
          margin: 14px 0 0 0;
          padding: 14px 0;
          background: transparent;
          background-clip: content-box;
          background-image: linear-gradient(
            to right,
            #27a2d6 0%,
            #27a2d6 50%,
            #4f9e6d 75%,
            #355e3b 100%
          );
          background-repeat: no-repeat;
          background-size: 100% 6px;
          background-position: center;
          border-radius: 999px;
          outline: none;
          cursor: pointer;
          touch-action: pan-x;
          -webkit-tap-highlight-color: transparent;
        }
        .pool-condition-slider::-webkit-slider-runnable-track,
        .pool-condition-slider::-moz-range-track {
          height: 6px;
          background: transparent;
        }
        .pool-condition-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 32px;
          height: 32px;
          margin-top: -13px;
          border-radius: 50%;
          background: #ffffff;
          border: 3px solid var(--accent-color, #27a2d6);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
          cursor: grab;
        }
        .pool-condition-slider::-moz-range-thumb {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #ffffff;
          border: 3px solid var(--accent-color, #27a2d6);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
          cursor: grab;
        }
        .pool-condition-ticks {
          display: flex;
          justify-content: space-between;
          margin-top: 0;
          padding: 0 2px;
        }
        .pool-condition-tick {
          background: none;
          border: none;
          padding: 6px;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
        }
        .pool-condition-tick-dot {
          display: block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #c7d2dc;
          transition: transform 150ms ease, background 150ms ease;
        }
        .pool-condition-tick.active .pool-condition-tick-dot {
          background: var(--accent-color, #27a2d6);
          transform: scale(1.4);
        }
        .pool-condition-description {
          margin-top: 18px;
          text-align: center;
          color: var(--secondary-text);
          font-size: 15px;
          line-height: 1.45;
        }
      `}</style>
    </div>
  );
};

export default SlidePoolCondition;
